<!-- Back Navigation -->
<div class="mb-4">
  <a routerLink="/maintenance/service-appointments" class="text-decoration-none">
    <i class="bi bi-arrow-left me-1"></i>Back to Service Appointments
  </a>
</div>

<!-- Page Header -->
<div class="mb-4">
  <h1 class="h2 mb-1 fw-bold">Request Service Appointment</h1>
  <p class="text-muted mb-0">Schedule a maintenance appointment for your drone</p>
</div>

<!-- Loading State -->
@if (isLoading) {
  <div class="d-flex justify-content-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>
}

@if (!isLoading) {
  <div class="row">
    <div class="col-12 col-lg-8">
      <div class="card">
        <div class="card-body p-4">
          <form [formGroup]="form" (ngSubmit)="onSubmit()">
            <!-- Drone Selection -->
            <div class="mb-3">
              <label for="droneId" class="form-label">Drone <span class="text-danger">*</span></label>
              <select
                id="droneId"
                class="form-select"
                formControlName="droneId"
                [class.is-invalid]="isFieldInvalid('droneId')"
              >
                <option value="">Select a drone...</option>
                @for (drone of drones; track drone.id) {
                  <option [value]="drone.id">
                    {{ drone.name || drone.modelName || 'Drone' }} - {{ drone.serialNumber }}
                  </option>
                }
              </select>
              @if (isFieldInvalid('droneId')) {
                <div class="invalid-feedback">{{ getFieldError('droneId') }}</div>
              }
              @if (drones.length === 0 && !isDronesLoading) {
                <small class="text-muted">No drones found in your organization.</small>
              }
            </div>

            <!-- Preferred Date -->
            <div class="mb-3">
              <label for="preferredDate" class="form-label">Preferred Date <span class="text-danger">*</span></label>
              <input
                id="preferredDate"
                type="date"
                class="form-control"
                formControlName="preferredDate"
                [min]="minDate"
                [class.is-invalid]="isFieldInvalid('preferredDate')"
              >
              @if (isFieldInvalid('preferredDate')) {
                <div class="invalid-feedback">{{ getFieldError('preferredDate') }}</div>
              }
            </div>

            <!-- Preferred Time -->
            <div class="mb-3">
              <label for="preferredTime" class="form-label">Preferred Time <span class="text-danger">*</span></label>
              <select
                id="preferredTime"
                class="form-select"
                formControlName="preferredTime"
                [class.is-invalid]="isFieldInvalid('preferredTime')"
              >
                <option value="">Select a time preference...</option>
                @for (option of preferredTimeOptions; track option.value) {
                  <option [value]="option.value">{{ option.label }}</option>
                }
              </select>
              @if (isFieldInvalid('preferredTime')) {
                <div class="invalid-feedback">{{ getFieldError('preferredTime') }}</div>
              }
            </div>

            <!-- Issue Description -->
            <div class="mb-4">
              <label for="issueDescription" class="form-label">Issue Description <span class="text-danger">*</span></label>
              <textarea appCharCounter
                id="issueDescription"
                class="form-control"
                formControlName="issueDescription"
                rows="4"
                placeholder="Describe the issue or maintenance needed (minimum 20 characters)..."
                [class.is-invalid]="isFieldInvalid('issueDescription')"
              ></textarea>
              @if (isFieldInvalid('issueDescription')) {
                <div class="invalid-feedback">{{ getFieldError('issueDescription') }}</div>
              }
            </div>

            <!-- Attachments -->
            <div class="mb-4">
              <label class="form-label">Attachments (optional)</label>
              <p class="text-muted small mb-2">
                Upload photos or documents related to the issue (max {{ maxFiles }} files, {{ formatFileSize(maxFileSize) }} each)
              </p>

              <!-- File Input -->
              <div class="mb-3">
                <input
                  type="file"
                  class="form-control"
                  id="fileInput"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  (change)="onFileSelect($event)"
                  [disabled]="selectedFiles.length >= maxFiles"
                >
              </div>

              <!-- Selected Files List -->
              @if (selectedFiles.length > 0) {
                <div class="list-group">
                  @for (selectedFile of selectedFiles; track selectedFile.name; let i = $index) {
                    <div class="list-group-item d-flex justify-content-between align-items-center">
                      <div class="d-flex align-items-center">
                        <i class="bi {{ getFileIcon(selectedFile.type) }} me-2 fs-5 text-muted"></i>
                        <div>
                          <div class="fw-medium">{{ selectedFile.name }}</div>
                          <small class="text-muted">{{ formatFileSize(selectedFile.size) }}</small>
                          @if (selectedFile.error) {
                            <small class="text-danger d-block">{{ selectedFile.error }}</small>
                          }
                        </div>
                      </div>
                      <div class="d-flex align-items-center">
                        @if (selectedFile.isUploading) {
                          <div class="spinner-border spinner-border-sm text-primary me-2" role="status">
                            <span class="visually-hidden">Uploading...</span>
                          </div>
                        }
                        @if (selectedFile.uploadedInfo) {
                          <i class="bi bi-check-circle text-success me-2"></i>
                        }
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          (click)="removeFile(i)"
                          [disabled]="selectedFile.isUploading"
                        >
                          <i class="bi bi-x"></i>
                        </button>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>

            <!-- Form Actions -->
            <div class="d-flex gap-2">
              <button
                type="submit"
                class="btn btn-primary"
                [disabled]="isSaving"
              >
                @if (isSaving) {
                  <span class="spinner-border spinner-border-sm me-2"></span>
                }
                Request Appointment
              </button>
              <button type="button" class="btn btn-outline-secondary" (click)="goBack()">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
}
